/** Planned attendance, its exceptions and linked execution use the same vocabulary everywhere. */
import { VisitBrowseRow, VisitBrowseState, VisitDisplayStatus, visitDisplayStatus, isFullyApproved } from '@jms/model';
import { defineConditionalTag } from '../../shared/define.conditional.tag';
import { STATUS_TONE_STYLES, statusToneStyle, type StatusTone } from '../../shared/status.tag.tones';
import { visitAgeTag } from './visit.age.tag';
import { visitApprovalTag } from './visit.approval.tag';
import { visitConstructionTag } from './visit.construction.tag';

const TONES: Record<VisitDisplayStatus, StatusTone> = {
  [VisitDisplayStatus.Planned]: 'neutral',
  [VisitDisplayStatus.Modified]: 'brand',
  [VisitDisplayStatus.Rescheduled]: 'brand',
  [VisitDisplayStatus.Skipped]: 'neutral',
  // A Job exists and is still Open: brand matches the Job board's own Open/Scheduled tone.
  [VisitDisplayStatus.Scheduled]: 'brand',
  [VisitDisplayStatus.InProgress]: 'warning',
  [VisitDisplayStatus.OnHold]: 'warning',
  [VisitDisplayStatus.Completed]: 'success',
  [VisitDisplayStatus.Cancelled]: 'neutral',
};

export const visitStateTag = defineConditionalTag<VisitBrowseRow>(row => {
  const label = visitDisplayStatus(row);
  return { label, tone: TONES[label] };
});

/** Pill colours that mean someone has to act; a chip wearing one outranks its status colour. */
const ATTENTION_COLOURS = new Set<string>([STATUS_TONE_STYLES.error.color, STATUS_TONE_STYLES.warning.color]);

/**
 * The Visit calendar chip's colour, first match wins:
 * - in trouble (Job not created, Missed, Overdue, Job overdue): the colour of the pill that says so;
 * - still Planned and Job-less: its approval pill's colour, because before a Job the approval is what changes
 *   (Not approved grey, Partly approved and Changed amber, Approved green, Auto-approved blue);
 * - otherwise its status pill's (Scheduled blue, In progress amber, Completed green, Skipped and Cancelled grey).
 */
export function visitStateColour(row: VisitBrowseRow): string | undefined {

  const attention = [visitConstructionTag(null, row), visitAgeTag(null, row)].find(tag => tag?.color && ATTENTION_COLOURS.has(tag.color));
  if (attention?.color) return attention.color;
  const status = visitDisplayStatus(row);
  // Null once a Job exists, so only pre-Job Visits take their approval's colour.
  const approval = PLANNED_STATUSES.has(status) ? visitApprovalTag(null, row) : null;
  return approval?.color ?? statusToneStyle(TONES[status]).color;
}

/** Statuses that still wait on approval; Skipped and Cancelled keep their own grey whatever was approved. */
const PLANNED_STATUSES = new Set<VisitDisplayStatus>([VisitDisplayStatus.Planned, VisitDisplayStatus.Modified, VisitDisplayStatus.Rescheduled]);

/** Approvals the stamper builds a whole Job from; Partly approved leaves work behind, so it is not one. */
/** A calendar chip draws dashed until its Visit is committed: a Job exists, or every task is approved. */
export function visitIsTentative(row: VisitBrowseRow): boolean {

  return !row.job?._id && !isFullyApproved(row.approvalStatus);
}

// InProgress and OnHold are the only raw VisitStatus values that print differently from their
// display label ('In progress', 'On hold'); the rest already match their VisitDisplayStatus string.
const STATUS_LABELS: Partial<Record<string, VisitDisplayStatus>> = {
  InProgress: VisitDisplayStatus.InProgress,
  OnHold: VisitDisplayStatus.OnHold,
};

/** A Job can add execution status; otherwise the primary Visit pill already names the lifecycle. */
export const visitStatusTag = defineConditionalTag<VisitBrowseRow>(row => {
  if (!row.job?._id || !row.status || row.status === 'Scheduled' || row.status === VisitBrowseState.Projected) return null;
  const label = STATUS_LABELS[row.status] ?? row.status;
  return { label, tone: TONES[label as VisitDisplayStatus] ?? 'neutral' };
});
